import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useLocalParticipant,
  useParticipants,
  useConnectionState,
  useTracks,
} from "@livekit/components-react";
import { Track, ConnectionState } from "livekit-client";
import "@livekit/components-styles";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useFeatureEnabled } from "@/lib/features";
import { toast } from "sonner";
import useWakeLock from "@/lib/useWakeLock";
import useCallMediaSession from "@/lib/useCallMediaSession";
import { Mic, MicOff, PhoneOff, PhoneCall, Loader2, UserPlus, Volume2, VolumeX } from "lucide-react";

/**
 * Member-to-member live audio powered by LiveKit Cloud. Two entry
 * points share this component:
 *
 *   <AudioCall kind="direct"      otherUserId={peerId} />
 *   <AudioCall kind="department"  departmentId={id}     />
 *
 * Life-cycle:
 *   idle → fetch(/api/voice/token) → connected (LiveKitRoom) → user
 *   ends the call → we tear the room down and reset to idle.
 *
 * We render an audio-only room (video={false}) with a mute toggle and a
 * hang-up button. Remote audio is played via <RoomAudioRenderer/> and
 * <StartAudio/> lifts the browser autoplay-block on iOS Safari after
 * the tap that started the call.
 */

export default function AudioCall({
  kind,
  otherUserId,
  departmentId,
  deptVoiceDisabled = false,
  deptAdminId,
  onClose,
  triggerLabel,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const voiceEnabled = useFeatureEnabled("voice", "member");
  const voiceDirectEnabled = useFeatureEnabled("voice_direct", "member");
  const voiceDeptEnabled = useFeatureEnabled("voice_department", "member");
  const [state, setState] = useState("idle"); // idle | fetching | connecting | live | error
  const [tokenPayload, setTokenPayload] = useState(null);
  const [error, setError] = useState("");
  // Department calls are gated on an "active session" started by the
  // dept admin. Non-admin members only see a JOIN button when a call
  // is live. We poll `/voice/dept/{id}/status` every 4s while idle.
  const [deptActive, setDeptActive] = useState(false);
  const isDeptModerator = kind === "department" && (
    user?.id === deptAdminId
    || user?.role === "main_admin"
    || user?.role === "super_admin"
  );

  useEffect(() => {
    if (kind !== "department" || !departmentId) return undefined;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await api.get(`/voice/dept/${departmentId}/status`);
        if (!stopped) setDeptActive(Boolean(r.data?.active));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("dept-call status poll error", err?.message);
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 4000);
    return () => { stopped = true; clearInterval(id); };
  }, [kind, departmentId]);

  // Master switch — church admin turned voice off entirely.
  if (!voiceEnabled) return null;
  // Per-kind master switches (added Feb 2026 — see PRD).
  if (kind === "direct" && !voiceDirectEnabled) return null;
  if (kind === "department" && !voiceDeptEnabled) return null;
  // Per-user outgoing block — this member cannot INITIATE calls at all
  // (they can still receive them; that's handled by IncomingCallListener).
  // For dept rooms we still allow JOINING an admin-started call — this
  // toggle is about starting, not participating.
  if (user?.voice_outgoing_disabled && kind === "direct") return null;
  if (user?.voice_outgoing_disabled && kind === "department" && !deptActive) return null;
  // Per-department block — group Call button hidden on THIS dept only.
  if (kind === "department" && deptVoiceDisabled) return null;

  const canCall = kind === "direct"
    ? Boolean(otherUserId && otherUserId !== user?.id)
    : Boolean(departmentId);

  const connect = async () => {
    if (!canCall) return;
    setState("fetching");
    setError("");
    try {
      const body = kind === "direct"
        ? { kind: "direct", other_user_id: otherUserId }
        : { kind: "department", department_id: departmentId };
      const r = await api.post("/voice/token", body);
      setTokenPayload(r.data);
      setState("connecting");
    } catch (e) {
      const msg = e?.response?.data?.detail || t("voiceStartError");
      setError(msg);
      setState("error");
      toast.error(msg);
    }
  };

  const hangup = () => {
    // For direct calls, fire-and-forget a "cancel" so the callee gets
    // an immediate missed-call bell (rather than waiting for the invite
    // TTL sweep). Safe to call for any hangup — if the callee already
    // picked up, the invite is no longer 'ringing' and the endpoint is
    // a no-op. We intentionally don't await this — the UI should tear
    // down instantly.
    if (kind === "direct" && otherUserId) {
      api.post(`/voice/outgoing/${otherUserId}/cancel`).catch(() => {
        // Best-effort. Callee's poll will still sweep it after TTL.
      });
    }
    setTokenPayload(null);
    setState("idle");
    setError("");
    onClose?.();
  };

  // Trigger visibility & label for department calls.
  //   • dept admin (or church admin): "Start call" when idle, "Join call" when call already live
  //   • regular member: only shown when call is live (label = "Join call")
  //   • no one else can even see the button (kind=direct handled below).
  let deptTriggerLabel = null;
  if (kind === "department") {
    if (isDeptModerator) {
      deptTriggerLabel = deptActive ? t("voiceJoinCall") : t("voiceStartCall");
    } else if (deptActive) {
      deptTriggerLabel = t("voiceJoinCall");
    }
    if (!deptTriggerLabel) return null; // regular member, no active call
  }

  // While idle we show the tap-to-call trigger; the modal panel appears
  // as soon as we start fetching a token.
  if (state === "idle" || state === "error") {
    return (
      <button
        type="button"
        onClick={connect}
        disabled={!canCall}
        data-testid={kind === "direct" ? "voice-call-direct-btn" : "voice-call-dept-btn"}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-white text-xs font-semibold shadow-sm disabled:opacity-50"
        style={{ backgroundColor: "var(--tenant-primary, #7A1F2B)" }}
      >
        <PhoneCall className="w-3.5 h-3.5" />
        {deptTriggerLabel || triggerLabel || t("voiceCall")}
      </button>
    );
  }

  return (
    <CallOverlay
      state={state}
      tokenPayload={tokenPayload}
      kind={kind}
      canModerate={isDeptModerator}
      departmentId={departmentId}
      onDisconnected={hangup}
      onConnected={() => setState("live")}
      onError={(msg) => {
        setError(msg);
        toast.error(msg);
        hangup();
      }}
    />
  );
}

/**
 * Bottom-sheet-style modal that overlays the app while a call is
 * active. Uses `position: fixed` so it survives navigation between
 * pages — the user can browse the dashboard with the call running in
 * the background.
 */
export function CallOverlay({ state, tokenPayload, kind, canModerate = false, departmentId, onConnected, onDisconnected, onError }) {
  const { t } = useI18n();
  // Keep the screen awake while the call/broadcast is live so an
  // auto-timeout doesn't kill the connection mid-conversation. The
  // hook is a no-op on browsers without the Wake Lock API.
  useWakeLock(Boolean(tokenPayload));
  // Register this tab as an active MediaSession so the OS keeps the
  // call audio flowing even when the user switches to another app or
  // locks the phone. Also surfaces lock-screen media controls so they
  // can hang up without unlocking.
  useCallMediaSession({
    active: Boolean(tokenPayload),
    title: tokenPayload?.display_name,
    onHangup: onDisconnected,
  });
  if (!tokenPayload) return null;
  // Listen-only participants (church-wide broadcast members) MUST NOT
  // publish audio. If we pass `audio={true}` here, LiveKit auto-tries
  // to enable the local mic on connect — but the token has
  // `can_publish=false`, so the SFU rejects the publish and the room
  // emits an error that our `onError` converts into an instant
  // hang-up. Passing `audio={false}` keeps the mic disabled and
  // subscription-only, which is exactly what a listener wants.
  const listenOnly = Boolean(tokenPayload.listen_only);

  return (
    <div
      className="fixed inset-x-0 z-[100] px-3 pointer-events-none"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
      data-testid="voice-call-overlay"
    >
      <div className="mx-auto max-w-md bg-stone-900 text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden pointer-events-auto">
        <LiveKitRoom
          audio={!listenOnly}
          video={false}
          token={tokenPayload.participant_token}
          serverUrl={tokenPayload.server_url}
          connect={true}
          onConnected={onConnected}
          onDisconnected={onDisconnected}
          onError={(e) => onError(e?.message || t("voiceStartError"))}
          // Keep the tree stable — LiveKit's docs warn that remounting
          // LiveKitRoom triggers client-initiated disconnects.
          options={{ adaptiveStream: true, dynacast: true }}
        >
          <RoomAudioRenderer />
          <CallPanel
            kind={kind}
            title={tokenPayload.display_name}
            roomName={tokenPayload.room_name}
            canModerate={canModerate}
            departmentId={departmentId}
            onHangup={onDisconnected}
          />
        </LiveKitRoom>
      </div>
    </div>
  );
}

function CallPanel({ kind, title, roomName, canModerate = false, departmentId, onHangup }) {
  const { t } = useI18n();
  const participants = useParticipants();
  const conn = useConnectionState();
  const { isMicrophoneEnabled, localParticipant } = useLocalParticipant();

  const remote = participants.filter((p) => !p.isLocal);
  const isConnecting = conn === ConnectionState.Connecting
    || conn === ConnectionState.Reconnecting
    || conn === ConnectionState.SignalReconnecting;

  // ── Call timer ────────────────────────────────────────────────────
  // Start counting when the FIRST remote joins the room (i.e. the peer
  // actually answered — parity with a regular phone call, where the
  // timer only starts after "connected"). Once counting, keep going
  // even if the remote briefly disconnects on flaky Wi-Fi so a hiccup
  // doesn't reset the clock.
  const [connectedAt, setConnectedAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const everHadRemoteRef = useRef(false);
  const peerLeftTimerRef = useRef(null);

  useEffect(() => {
    if (remote.length > 0 && !connectedAt) {
      setConnectedAt(Date.now());
      everHadRemoteRef.current = true;
    }
  }, [remote.length, connectedAt]);

  // ── Ring timeout for direct calls ─────────────────────────────────
  // If the callee doesn't pick up within 20 seconds we auto-hang so
  // the caller isn't left listening to a silent room forever. Only
  // applies to 1-to-1 calls — department rooms stay open indefinitely
  // because moderators use them as always-on channels. Timer clears
  // the moment the peer joins (connectedAt gets set) or the call is
  // ended by any other path.
  const [ringTimedOut, setRingTimedOut] = useState(false);
  useEffect(() => {
    if (kind !== "direct") return undefined;
    if (connectedAt) return undefined; // peer answered — no timeout
    const id = window.setTimeout(() => {
      setRingTimedOut(true);
      toast(t("voiceNoAnswer"));
      onHangup?.();
    }, 20000);
    return () => window.clearTimeout(id);
  }, [kind, connectedAt, onHangup, t]);

  // ── Ringback tone (caller side) ────────────────────────────────────
  // Play a soft US-style ringback (dual 440 Hz + 480 Hz sine, 2 s on /
  // 4 s off) to the CALLER while the direct call is ringing. Stops the
  // instant the peer joins, we hang up, or the ring timeout fires.
  // Using WebAudio means no audio file to ship and no autoplay policy
  // headache — the caller already tapped "Call", which is the required
  // user gesture to start an AudioContext.
  useEffect(() => {
    if (kind !== "direct") return undefined;
    if (connectedAt || ringTimedOut) return undefined;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return undefined;
    let ctx;
    let osc1;
    let osc2;
    let gain;
    try {
      ctx = new AC();
      ctx.resume?.().catch(() => { /* iOS may still be suspending */ });
      gain = ctx.createGain();
      // Very soft — this plays through the phone's speaker while the
      // member is holding the phone to their ear, so anything above
      // ~0.06 is uncomfortable. Real telcos target around -20 dBFS.
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      osc1 = ctx.createOscillator();
      osc1.type = "sine";
      osc1.frequency.value = 440;
      osc2 = ctx.createOscillator();
      osc2.type = "sine";
      osc2.frequency.value = 480;
      osc1.connect(gain);
      osc2.connect(gain);
      osc1.start();
      osc2.start();
      // Schedule the ring pattern ahead by 60 seconds — well past the
      // 20 s ring timeout, but if we never reach it the cleanup below
      // still cancels the graph cleanly.
      const startAt = ctx.currentTime;
      const RING_ON = 2;   // seconds tone is on
      const RING_OFF = 4;  // seconds silence between rings
      for (let i = 0; i < 12; i += 1) {
        const t0 = startAt + i * (RING_ON + RING_OFF);
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(0.055, t0 + 0.05);
        gain.gain.setValueAtTime(0.055, t0 + RING_ON - 0.05);
        gain.gain.linearRampToValueAtTime(0, t0 + RING_ON);
      }
    } catch (_) {
      /* if WebAudio setup fails we simply have no ringback — not
         fatal, the visual "Ringing…" subtitle still communicates state */
    }
    return () => {
      try { osc1?.stop(); } catch (_) { /* already stopped */ }
      try { osc2?.stop(); } catch (_) { /* already stopped */ }
      try { gain?.disconnect(); } catch (_) { /* ignore */ }
      try { ctx?.close(); } catch (_) { /* ignore */ }
    };
  }, [kind, connectedAt, ringTimedOut]);

  // Tick the "now" clock once per second, but only while a call is in
  // progress — no wasted intervals when we're just ringing.
  useEffect(() => {
    if (!connectedAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [connectedAt]);

  const elapsedSec = connectedAt ? Math.max(0, Math.floor((now - connectedAt) / 1000)) : 0;
  const durationLabel = useMemo(() => {
    const h = Math.floor(elapsedSec / 3600);
    const m = Math.floor((elapsedSec % 3600) / 60);
    const s = elapsedSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  }, [elapsedSec]);

  // ── Peer-hangup detection for direct calls ─────────────────────────
  // In a 1-to-1 room, if the OTHER side leaves after the call started,
  // that's them ending the call. Show a friendly notice and auto-close
  // ~1.2s later so the local party doesn't get stranded in an empty
  // room silently. For department rooms this is normal churn — leave
  // those alone (people join and leave freely).
  const [peerEnded, setPeerEnded] = useState(false);
  useEffect(() => {
    if (kind !== "direct") return undefined;
    if (peerLeftTimerRef.current) {
      window.clearTimeout(peerLeftTimerRef.current);
      peerLeftTimerRef.current = null;
    }
    if (everHadRemoteRef.current && remote.length === 0) {
      setPeerEnded(true);
      peerLeftTimerRef.current = window.setTimeout(() => {
        toast(t("voiceCallEnded"));
        onHangup?.();
      }, 1200);
    } else {
      setPeerEnded(false);
    }
    return () => {
      if (peerLeftTimerRef.current) {
        window.clearTimeout(peerLeftTimerRef.current);
        peerLeftTimerRef.current = null;
      }
    };
  }, [kind, remote.length, onHangup, t]);

  const subtitle = useMemo(() => {
    if (ringTimedOut) return t("voiceNoAnswer");
    if (peerEnded) return t("voiceCallEnded");
    if (isConnecting) return t("voiceConnecting");
    if (kind === "church_broadcast") {
      // Listeners see "You are listening"; the publishing admin sees a
      // live listener count so they know how many members are tuned in.
      const isPublisher = Boolean(localParticipant?.permissions?.canPublish);
      if (isPublisher) {
        return `${remote.length} ${t("broadcastListeningNow")}`;
      }
      return t("broadcastListening");
    }
    if (kind === "direct") {
      if (remote.length === 0) return t("voiceRinging");
      const p = remote[0];
      return p.isSpeaking ? t("voiceSpeaking") : t("voiceInCall");
    }
    if (remote.length === 0) return t("voiceRoomWaiting");
    return `${remote.length + 1} ${t("voiceInRoom")}`;
  }, [ringTimedOut, peerEnded, isConnecting, kind, remote, localParticipant, t]);

  const toggleMic = async () => {
    try {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("mic toggle failed", err?.message);
      }
    }
  };

  // Playback volume for every REMOTE audio track in the room. Starts
  // muted OFF (1.0). Persist to localStorage so a member's preferred
  // level survives navigation and reconnects. LiveKit exposes per-track
  // setVolume(0..1) on RemoteAudioTrack; we iterate all subscribed mic
  // tracks and mirror the slider. Volume=0 acts as a temporary output
  // mute (different from unsubscribing).
  const audioTracks = useTracks([Track.Source.Microphone], { onlySubscribed: true });
  const [volume, setVolume] = useState(() => {
    if (typeof localStorage === "undefined") return 1;
    const v = parseFloat(localStorage.getItem("ch_call_volume") || "1");
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
  });
  useEffect(() => {
    for (const tr of audioTracks) {
      const track = tr.publication?.track;
      if (track && typeof track.setVolume === "function" && !tr.participant?.isLocal) {
        try { track.setVolume(volume); } catch { /* new tracks may not be ready */ }
      }
    }
  }, [volume, audioTracks]);
  const changeVolume = (v) => {
    setVolume(v);
    try { localStorage.setItem("ch_call_volume", String(v)); } catch { /* ignore */ }
  };

  return (
    <div className="px-4 py-3" data-testid="voice-call-panel">
      <div className="flex items-center justify-between gap-3 min-h-[44px]">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-white/60 truncate">
            {kind === "direct"
              ? t("voiceDirectCall")
              : kind === "church_broadcast"
                ? t("broadcastLive")
                : t("voiceDeptRoom")}
          </div>
          <div className="font-serif-display text-base truncate" data-testid="voice-call-title">{title}</div>
          <div className="text-[11px] text-white/70 mt-0.5 flex items-center gap-2 truncate" data-testid="voice-call-subtitle">
            <span className="flex items-center gap-1.5 truncate">
              {isConnecting && <Loader2 className="w-3 h-3 animate-spin shrink-0" />}
              <span className="truncate">{subtitle}</span>
            </span>
            {connectedAt && (
              <span
                data-testid="voice-call-duration"
                className="tabular-nums text-white/80 font-medium px-1.5 py-0.5 rounded bg-white/10 shrink-0"
              >
                {durationLabel}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {localParticipant?.permissions?.canPublish !== false && (
            <button
              type="button"
              onClick={toggleMic}
              data-testid="voice-call-mute"
              aria-label={isMicrophoneEnabled ? t("voiceMute") : t("voiceUnmute")}
              className={`w-11 h-11 rounded-full grid place-items-center transition ${
                isMicrophoneEnabled
                  ? "bg-white/10 hover:bg-white/20"
                  : "bg-amber-500/30 hover:bg-amber-500/40"
              }`}
            >
              {isMicrophoneEnabled ? (
                <Mic className="w-5 h-5" />
              ) : (
                <MicOff className="w-5 h-5" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onHangup}
            data-testid="voice-call-hangup"
            aria-label={t("voiceHangup")}
            className="w-11 h-11 rounded-full grid place-items-center bg-red-600 hover:bg-red-500 transition"
          >
            <PhoneOff className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Playback volume — applies to every remote audio track in the
          room. VolumeX icon on the left toggles a temp mute (volume=0),
          the slider adjusts 0-100%. Value persisted in localStorage
          under `ch_call_volume`. */}
      <div className="mt-3 flex items-center gap-2" data-testid="voice-call-volume">
        <button
          type="button"
          onClick={() => changeVolume(volume === 0 ? 1 : 0)}
          data-testid="voice-call-volume-toggle"
          aria-label={volume === 0 ? t("voiceUnmute") : t("voiceMute")}
          className="w-7 h-7 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 shrink-0"
        >
          {volume === 0 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(volume * 100)}
          onChange={(e) => changeVolume(Number(e.target.value) / 100)}
          data-testid="voice-call-volume-slider"
          aria-label={t("voiceVolume")}
          className="flex-1 h-1 accent-white/80 cursor-pointer"
        />
        <span className="text-[10px] text-white/50 w-8 text-right tabular-nums">
          {Math.round(volume * 100)}%
        </span>
      </div>

      {/* Participant list (dept rooms only). We hide it for 1-to-1
          since the two names are already in the header title. */}
      {kind === "department" && remote.length > 0 && (
        <ul className="mt-3 border-t border-white/10 pt-2 space-y-1" data-testid="voice-call-participants">
          {[localParticipant, ...remote].map((p) => (
            <ParticipantRow
              key={p.identity}
              p={p}
              roomName={roomName}
              canModerate={canModerate}
            />
          ))}
        </ul>
      )}

      {/* Mobile Safari needs a user-gesture handshake before playing
          remote audio. If autoplay was blocked, this shows a small
          "Enable audio" button. Silent otherwise. */}
      <div className="mt-2">
        <StartAudio label={t("voiceEnableAudio")} className="lk-start-audio-inline" />
      </div>

      {/* Moderator actions: end call for everyone + invite non-member.
          Only visible for dept moderators (dept admin + church admins).
          Regular members just get the personal Hang up button above. */}
      {kind === "department" && canModerate && departmentId && (
        <ModeratorActions departmentId={departmentId} onEnded={onHangup} />
      )}
    </div>
  );
}

function ModeratorActions({ departmentId, onEnded }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const endForAll = async () => {
    if (busy) return;
    // Hard confirmation — kills the room for every participant.
    if (!window.confirm(t("voiceEndForAllConfirm"))) return;
    setBusy(true);
    try {
      await api.post(`/voice/dept/${departmentId}/end`);
      toast.success(t("voiceEnded"));
      onEnded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voiceStartError"));
    } finally {
      setBusy(false);
    }
  };

  const openInvite = async () => {
    setInviting(true);
    setLoadingList(true);
    try {
      // Pull the church-wide member list so the admin can invite
      // ANYONE, including non-dept members (per spec).
      const r = await api.get("/members");
      setCandidates(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voiceStartError"));
    } finally {
      setLoadingList(false);
    }
  };

  const invite = async (userId) => {
    try {
      await api.post(`/voice/dept/${departmentId}/invite`, { user_id: userId });
      toast.success(t("voiceInviteSent"));
      setInviting(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voiceStartError"));
    }
  };

  return (
    <>
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={openInvite}
          data-testid="voice-invite-btn"
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-white/10 hover:bg-white/20 transition"
        >
          <UserPlus className="w-3.5 h-3.5" />
          {t("voiceInvite")}
        </button>
        <button
          type="button"
          onClick={endForAll}
          disabled={busy}
          data-testid="voice-end-for-all-btn"
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-red-600 hover:bg-red-500 transition disabled:opacity-50"
        >
          <PhoneOff className="w-3.5 h-3.5" />
          {t("voiceEndForAll")}
        </button>
      </div>

      {inviting && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 flex items-end sm:items-center justify-center p-4"
          data-testid="voice-invite-picker"
          onClick={() => setInviting(false)}
        >
          <div
            className="w-full max-w-sm bg-white text-stone-900 rounded-2xl overflow-hidden max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-stone-100 font-serif-display text-base">
              {t("voiceInvitePickerTitle")}
            </div>
            {loadingList ? (
              <div className="px-4 py-6 text-center text-sm text-stone-500">{t("loading")}</div>
            ) : (
              <ul className="overflow-y-auto divide-y divide-stone-100 flex-1">
                {candidates.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-stone-50"
                      data-testid={`voice-invite-pick-${m.id}`}
                      onClick={() => invite(m.id)}
                    >
                      <div className="w-8 h-8 rounded-full bg-stone-100 overflow-hidden shrink-0">
                        {m.photo_url ? (
                          <img src={m.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-stone-400 text-xs font-semibold">
                            {(m.name || "?")[0]?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{m.name}</div>
                        <div className="text-[11px] text-stone-500 truncate">{m.email}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setInviting(false)}
              className="w-full h-11 text-sm border-t border-stone-100 hover:bg-stone-50"
              data-testid="voice-invite-cancel"
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * One row in the dept-voice-room participant list. When `canModerate`
 * is true and the row is NOT the local user, we render a small mic
 * button that force-mutes (or unmutes) the remote participant via the
 * `POST /voice/rooms/{room}/participants/{identity}/mute` endpoint.
 *
 * `p.isMicrophoneEnabled` refreshes automatically on the moderator's
 * client thanks to LiveKit's TrackMuted events, so the icon stays
 * correct without any extra state.
 */
function ParticipantRow({ p, roomName, canModerate }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const canMuteThisRow = canModerate && !p.isLocal;
  const isMuted = !p.isMicrophoneEnabled;

  const toggleRemoteMute = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(
        `/voice/rooms/${encodeURIComponent(roomName)}/participants/${encodeURIComponent(p.identity)}/mute`,
        { mute: !isMuted },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("voiceMuteFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className="flex items-center justify-between text-xs gap-2"
      data-testid={`voice-participant-${p.identity}`}
    >
      <span className="truncate flex-1 min-w-0">
        {p.name || p.identity}
        {p.isLocal && <span className="text-white/40"> · {t("voiceYou")}</span>}
      </span>
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
          p.isSpeaking ? "bg-emerald-400" : "bg-white/20"
        }`}
        aria-hidden
      />
      {canMuteThisRow && (
        <button
          type="button"
          onClick={toggleRemoteMute}
          disabled={busy}
          data-testid={`voice-mute-participant-${p.identity}`}
          aria-label={isMuted ? t("voiceUnmuteParticipant") : t("voiceMuteParticipant")}
          title={isMuted ? t("voiceUnmuteParticipant") : t("voiceMuteParticipant")}
          className={`w-7 h-7 rounded-full grid place-items-center transition shrink-0 disabled:opacity-40 ${
            isMuted ? "bg-amber-500/30 hover:bg-amber-500/40" : "bg-white/10 hover:bg-white/20"
          }`}
        >
          {isMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
        </button>
      )}
    </li>
  );
}


/**
 * INLINE LiveKit room + optional mic-mute button used by the
 * main_admin while they broadcast to the church. Renders NO
 * call-modal chrome — the room's audio is wired invisibly, and a
 * children render-prop hands out `<MicMuteButton />` so the caller
 * can drop the mic control inline in their own layout.
 *
 * The reason we don't reuse `<CallOverlay>` here is UX: the overlay is
 * a fixed floating panel that visually reads as "you have an incoming
 * call". Broadcasters found that confusing — they thought they were
 * both broadcasting AND receiving a call at the same time. This
 * component keeps the admin's controls inside the ChurchBroadcastBar
 * so there is exactly one on-screen surface for the broadcast.
 */
export function BroadcastRoom({ tokenPayload, onDisconnected, onError, children }) {
  const { t } = useI18n();
  useWakeLock(Boolean(tokenPayload));
  useCallMediaSession({
    active: Boolean(tokenPayload),
    title: tokenPayload?.display_name || t("liveBroadcast") || "Live broadcast",
    onHangup: onDisconnected,
  });
  if (!tokenPayload) return <>{children}</>;
  return (
    <LiveKitRoom
      audio={true}
      video={false}
      token={tokenPayload.participant_token}
      serverUrl={tokenPayload.server_url}
      connect={true}
      onDisconnected={onDisconnected}
      onError={(e) => onError?.(e?.message || t("voiceStartError"))}
      options={{ adaptiveStream: true, dynacast: true }}
      className="contents"
    >
      {/* Publisher: no remote audio to play back — a broadcast is
          one-way. Skipping RoomAudioRenderer avoids the admin
          hearing anything on their own speaker (there's nothing
          to hear anyway; listeners cannot publish). */}
      {children}
    </LiveKitRoom>
  );
}

/**
 * Inline mic mute pill that hooks into the surrounding LiveKitRoom.
 * Falls back to a disabled visual state when there is no room
 * context (e.g. the admin hasn't started the broadcast yet).
 */
export function BroadcastMicButton() {
  const { t } = useI18n();
  const local = useLocalParticipant();
  const enabled = local?.isMicrophoneEnabled;
  const toggle = async () => {
    try { await local?.localParticipant?.setMicrophoneEnabled(!enabled); } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="broadcast-mic-toggle"
      aria-label={enabled ? t("voiceMute") : t("voiceUnmute")}
      title={enabled ? t("voiceMute") : t("voiceUnmute")}
      className={`w-9 h-9 rounded-full grid place-items-center shrink-0 transition ${
        enabled ? "bg-white text-red-700 border border-red-600/40" : "bg-amber-500 text-white"
      }`}
    >
      {enabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
    </button>
  );
}
